import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import { FaFilePdf, FaDownload } from 'react-icons/fa';
import API_BASE_URL from '../../../config/api';

const MyReviews = () => {
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseUrl = API_BASE_URL;

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/key-result-areas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const allGoals = (Array.isArray(response.data) ? response.data : [])
          .flatMap(kra => Array.isArray(kra.goals) ? kra.goals.map(g => ({ ...g, kra_name: kra.name || kra.title })) : [])
          .filter(goal => goal.status === 'complete' || goal.status === 'completed');
        setGoals(allGoals);
        const years = Array.from(new Set(allGoals.map(g => g?.year).filter(y => y !== undefined && y !== null))).sort((a,b)=>a-b);
        setAvailableYears(years);
        const effectiveYear = years.length && years.includes(selectedYear) ? selectedYear : (years[years.length-1] || selectedYear);
        setSelectedYear(effectiveYear);
        setFilteredGoals(allGoals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === effectiveYear : true));
      } catch (err) {
        setError('Failed to fetch KPIs.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, []);

  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(goals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === selectedYear : true));
    }
  }, [selectedYear, goals]);

  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });
    const totalHod = filteredGoals.reduce((s,g)=> s + (Number(g.hod_score)||0),0);
    const totalMetric = filteredGoals.reduce((s,g)=> s + (Number(g.metric)||0),0);
    const percentage = totalMetric > 0 ? ((totalHod/totalMetric)*100).toFixed(2) : '0.00';
    return { year: selectedYear, kras: Object.entries(kraMap), totalHod, totalMetric, percentage, goals: filteredGoals };
  };

  const generatePDF = async () => {
    try {
      const report = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p','mm','a4');
      const primary = [59,130,246];
      const dark = [55,65,81];
      const light = [156,163,175];
      const wrap = (text, maxWidth) => {
        const words = String(text||'').split(' ');
        const lines=[]; let line='';
        words.forEach(w=>{ const t=line?(line+' '+w):w; if(doc.getTextWidth(t)>maxWidth && line){ lines.push(line); line=w; } else { line=t; } });
        if(line) lines.push(line); return lines;
      };
      const drawTable = (headers, rows, startY, maxWidth=180) => {
        const colWidths = [70,40,30,30,10];
        const headerH=10; const rowH=8; let x=15;
        doc.setFillColor(...primary); doc.rect(15,startY,maxWidth,headerH,'F');
        headers.forEach((h,i)=>{ doc.setTextColor(255,255,255); doc.setFontSize(10); doc.setFont(undefined,'bold'); doc.text(h, x+2, startY+6); x+=colWidths[i]; });
        let y = startY+headerH; doc.setTextColor(0,0,0); doc.setFont(undefined,'normal'); doc.setFontSize(9);
        rows.forEach((row, rIdx)=>{
          if(rIdx%2===0){ doc.setFillColor(249,250,251); doc.rect(15,y,maxWidth,rowH,'F'); }
          let xx=15; let maxLines=1;
          row.forEach((cell,i)=>{ const wl = wrap(cell, colWidths[i]-4); maxLines=Math.max(maxLines, wl.length); wl.forEach((ln,li)=>{ doc.text(ln, xx+2, y+5+(li*3.5)); }); xx+=colWidths[i]; });
          y += Math.max(rowH, maxLines*3.5+2);
        });
        return y;
      };
      let y=20;
      doc.setTextColor(...primary); doc.setFontSize(18); doc.setFont(undefined,'bold');
      doc.text('KPI Reviews Report', 105, y, {align:'center'});
      y+=12; doc.setTextColor(...dark); doc.setFontSize(14); doc.setFont(undefined,'normal');
      doc.text(`Year: ${report.year}`, 105, y, {align:'center'});
      y+=16; doc.setFillColor(248,250,252); doc.rect(15,y,180,25,'F'); doc.setDrawColor(...primary); doc.rect(15,y,180,25);
      y+=8; doc.setTextColor(...dark); doc.setFontSize(13); doc.setFont(undefined,'bold'); doc.text('Summary', 20, y);
      y+=7; doc.setFontSize(11); doc.setFont(undefined,'normal'); doc.text(`Total HOD Score: ${report.totalHod} / ${report.totalMetric}`, 20, y);
      y+=5; doc.text(`Performance: ${report.percentage}%`, 20, y);
      y+=15;
      report.kras.forEach(([kraName, kraGoals])=>{
        if(y>250){ doc.addPage(); y=20; }
        doc.setFillColor(...primary); doc.rect(15,y,180,12,'F'); doc.setTextColor(255,255,255); doc.setFontSize(12); doc.setFont(undefined,'bold');
        doc.text(`KRA: ${kraName}`, 20, y+8); y+=15;
        const headers = ['Activity/KPI','Standard','Metric','HOD',''];
        const rows = kraGoals.map((g,i)=>[
          g.activity || 'N/A',
          g.performance_standard || 'N/A',
          (g.metric !== undefined ? g.metric : '-'),
          (g.hod_score !== undefined ? g.hod_score : '-'),
          ''
        ]);
        const kraTotal = kraGoals.reduce((s,g)=> s+(Number(g.hod_score)||0),0);
        const kraMetric = kraGoals.reduce((s,g)=> s+(Number(g.metric)||0),0);
        rows.push([`KRA Total`, '', `${kraMetric}`, `${kraTotal}`, '']);
        y = drawTable(headers, rows, y);
        y+=10;
      });
      const pages = doc.internal.getNumberOfPages();
      for(let i=1;i<=pages;i++){
        doc.setPage(i); doc.setTextColor(...light); doc.setFontSize(8);
        doc.text('Generated by PMS', 15, 290);
        doc.text(`Page ${i} of ${pages}`, 195, 290, {align:'right'});
      }
      const filename = `KPI Reviews Report ${selectedYear}.pdf`;
      doc.save(filename);
    } catch (e) {
      alert('Failed to generate PDF');
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
      <div className="hr-content-wrapper" style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div className="page-header">
        <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="page-title">
            <h1 style={{ margin: 0 }}>KPI Reviews</h1>
            <p style={{ margin: 0 }}>View your completed KPIs and HOD scores</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 17, color: '#fff' }}>Year</label>
              <select
                value={selectedYear}
                onChange={(e)=>setSelectedYear(parseInt(e.target.value))}
                style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#0f172a', fontSize: 14, cursor: 'pointer' }}
              >
                {(availableYears.length ? availableYears : [selectedYear]).sort((a,b)=>a-b).map(y=> (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {filteredGoals.length > 0 && (
              <button onClick={generatePDF} title="Download PDF Report" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
                <FaFilePdf style={{ fontSize: 16 }} />
                <FaDownload style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
        </div>
      </div>
        {loading ? (
          <div style={{ color: '#64748b', fontSize: 18 }}>Loading...</div>
        ) : error ? (
          <div style={{ color: '#ef4444', background: '#fef2f2', borderRadius: 4, padding: '10px 18px', fontSize: 16 }}>{error}</div>
        ) : filteredGoals.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 18 }}>No completed KPIs found.</div>
        ) : (
          (() => {
            const kraMap = {};
            filteredGoals.forEach(g => {
              const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
              if (!kraMap[kraName]) kraMap[kraName] = [];
              kraMap[kraName].push(g);
            });
            const grouped = Object.entries(kraMap);
            return (
              <div style={{marginTop: 24}} className="goals-table-wrapper">
                <table className="goals-table">
                  <thead>
                    <tr>
                      <th>Activity / Key Performance Indicator</th>
                      <th>Performance Standard / Target</th>
                      <th>Metric</th>
                      <th>Self Score</th>
                      <th>COO Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(([kra, kraGoals], gidx) => (
                      <React.Fragment key={kra || gidx}>
                        <tr style={{ background: '#fffb' }}>
                          <th colSpan={6} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#334155', width: '100%' }}>
                            KRA: {kra}
                          </th>
                        </tr>
                        {kraGoals.map((goal, idx) => (
                          <tr key={goal.id || idx}>
                            <td>{goal.activity || '[No Activity]'}</td>
                            <td>{goal.performance_standard !== undefined ? goal.performance_standard : '-'}</td>
                            <td>{goal.metric !== undefined ? goal.metric : '-'}</td>
                            <td>{goal.score !== undefined ? goal.score : '-'}</td>
                            <td>{goal.hod_score !== undefined ? goal.hod_score : '-'}</td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, background: '#f8fafc' }}>KRA Final</td>
                          <td style={{ fontWeight: 800, color: '#0f172a', background: '#f8fafc' }}>
                            {kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)} / {kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                          </td>
                          <td style={{ background: '#f8fafc' }}></td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {/* Final totals summary */}
                {filteredGoals.length > 0 ? (() => {
                  const totalHod = filteredGoals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
                  const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
                  const pct = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
                  return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '10px 16px',
                        boxShadow: '0 2px 8px rgba(30,41,59,0.06)'
                      }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>Final Total</div>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{totalHod} / {totalMetric}</div>
                        <div style={{
                          marginLeft: 8,
                          background: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 800,
                          padding: '4px 10px',
                          borderRadius: 9999,
                          border: '1px solid #bae6fd'
                        }}>{pct}%</div>
                      </div>
                    </div>
                  );
                })() : null}
              </div>
            );
          })()
        )}
        </div>        
      </div>
    </>
  );
}

export default MyReviews;
